import React, { useState, useEffect } from "react";
import { Button, Select, Spin, message, Input, Form } from "antd";
import { createCountry, createCountryAlias, getAllCountries, updateException } from "../api";
import { ExceptionStatus } from "../ExceptionDrawer";
import Hide from "../../components/Hide";

const { Option } = Select;

const UnknownCountryComponent = ({ exception, onClose, onResolved }) => {
    const [countryId, setCountryId] = useState('');
    const [countryName, setCountryName] = useState('');
    const [showSelect, setShowSelect] = useState(false);
    const [countries, setCountries] = useState([]);
    const [selectedAliasId, setSelectedAliasId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [form] = Form.useForm();

    useEffect(() => {
        const extractCountryDetails = () => {
            const unknownInfo = exception?.unknown_infomation || '';
            setCountryId(unknownInfo);
            setCountryName(unknownInfo);
            setTimeout(() => {
                form.setFieldsValue({
                    countryId: unknownInfo,
                    countryName: unknownInfo
                });
            }, 100);
        };

        if (exception) {
            extractCountryDetails();
        }

        const fetchCountries = async () => {
            setLoading(true);
            try {
                const response = await getAllCountries();
                const countriesData = response?.data?.data || [];
                setCountries(Array.isArray(countriesData) ? countriesData : []);
            } catch (error) {
                message.error('Failed to fetch countries');
            } finally {
                setLoading(false);
            }
        };

        fetchCountries();
    }, [exception]);

    const handleMarkAsNew = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);
            
            if (exception.status === 'pending') {
                const payload = {
                    id: values.countryId,
                    name: values.countryName,
                    alias_for: null
                };
                
                await createCountry(payload);
                await updateException(exception.id);
                message.success('Country created');
                try { if (typeof onResolved === 'function') await onResolved(); } catch {}
                onClose();
            } else {
                try { if (typeof onResolved === 'function') await onResolved(); } catch {}
                onClose();
            }
        } catch (error) {
            if (error.errorFields) {
                message.error('Please fill in all required fields');
            } else {
                const errorMessage = error.response?.data?.message || 'Failed to create country';
                message.error(errorMessage);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleMarkAsAliasFor = () => {
        setShowSelect(true);
    };

    const handleCountrySelect = (value) => {
        setSelectedAliasId(value ?? null);
    };

    const handleSaveAlias = async () => {
        if (!selectedAliasId) return;
        
        try {
            const values = await form.validateFields();
            setLoading(true);
            
            if (exception.status === 'pending') {
                const payload = {
                    id: values.countryId,
                    name: values.countryName,
                    alias_for: selectedAliasId
                };
                
                await createCountryAlias(payload);
                await updateException(exception.id);
                message.success('Alias saved');
                try { if (typeof onResolved === 'function') await onResolved(); } catch {}
                onClose();
            } else {
                try { if (typeof onResolved === 'function') await onResolved(); } catch {}
                onClose();
            }
        } catch (error) {
            if (error.errorFields) {
                message.error('Please fill in all required fields');
            } else {
                const errorMessage = error.response?.data?.message || 'Failed to save alias';
                message.error(errorMessage);
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{ marginTop: 20 }}>
            <Hide condition={exception.status === ExceptionStatus.RESOLVED}>
                <Spin spinning={loading} tip="Processing...">
                    <Form form={form} layout="vertical" style={{ marginBottom: 20 }}>
                        <Form.Item
                            label="Country ID"
                            name="countryId"
                            rules={[{ required: true, message: 'Please enter country ID' }]}
                        >
                            <Input placeholder="Enter country ID (e.g., TZ)" />
                        </Form.Item>
                        
                        <Form.Item
                            label="Country Name"
                            name="countryName"
                            rules={[{ required: true, message: 'Please enter country name' }]}
                        >
                            <Input placeholder="Enter country name (e.g., Tanzania)" />
                        </Form.Item>
                    </Form>

                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                        <Button 
                            type="primary" 
                            onClick={handleMarkAsNew}
                            disabled={loading}
                            loading={loading}
                        >
                            Mark as New
                        </Button>
                        <Button 
                            type="default" 
                            onClick={handleMarkAsAliasFor}
                            disabled={loading}
                        >
                            Mark as Alias For
                        </Button>
                        {showSelect && (
                            <>
                                <div style={{ fontSize: 12, color: '#666', marginTop: 16, marginBottom: 8 }}>
                                    Select an existing country to alias the new country to:
                                </div>
                                <div style={{ fontSize: 12, color: '#999', marginBottom: 8 }}>
                                    Debug: showSelect={showSelect.toString()}, countries count={countries.length}
                                </div>
                                <Select
                                    style={{ minWidth: 220 }}
                                    placeholder="Select a country"
                                    onChange={handleCountrySelect}
                                    disabled={loading}
                                    value={selectedAliasId ?? undefined}
                                >
                                    {countries.map((country) => (
                                        <Option key={country.id} value={country.id}>
                                            {country.name} ({country.id})
                                        </Option>
                                    ))}
                                </Select>
                                <Button
                                    type="primary"
                                    onClick={handleSaveAlias}
                                    disabled={loading || !selectedAliasId}
                                    loading={loading}
                                >
                                    Save Alias
                                </Button>
                            </>
                        )}
                    </div>
                </Spin>
            </Hide>
        </div>
    );
};

export default UnknownCountryComponent;
